/**
 * Gün sonu Z raporu — KK-603, AK-13, KK-608, KK-810.
 *
 * =============================================================================
 * KONSOLİDE, KIRILIMIN TOPLAMIDIR
 * -----------------------------------------------------------------------------
 * AK-13: aynı cüzdandan iki farklı noktada yapılan harcama, HER NOKTANIN kendi
 * cirosunda ayrı, konsolidede toplam görünür. Sunucu konsolideyi ayrı bir
 * sorgudan değil, nokta kırılımının toplamından üretir; ekran da aynı sırayı
 * korur: önce satır satır noktalar, en altta konsolide. Kırılımı gizleyip
 * yalnızca toplam göstermek, "hangi nokta" sorusunu ekrandan silmek olurdu.
 *
 * VARDİYA FARKI özel bir alandır: hiçbir vardiya kapanmadıysa null döner ve
 * ekranda "vardiya kapanmadı" yazar. Buraya 0,00 yazmak "sayım yapıldı ve tam
 * tuttu" demektir — ve yanlıştır.
 *
 * YAZDIRMA: kabuk print:hidden'dir; kâğıda yalnızca başlık ve tablo çıkar.
 * =============================================================================
 */
import { useEffect, useState } from "react";
import {
  Rozet,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { yerelGun } from "@kantin/shared";
import type { ZRaporuNokta } from "@kantin/shared";
import { oturumDurumu } from "../../veri/oturum.js";
import { useOkul, useZRaporu } from "../../veri/sorgular.js";
import { gunGoster, yerelBugun } from "../../veri/bicim.js";
import {
  DisaAktar,
  FiltreSeridi,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
} from "../../ortak.js";

export function ZRaporu() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const okul = useOkul();
  const [tarih, setTarih] = useState(yerelBugun());
  const [dokunuldu, setDokunuldu] = useState(false);

  // "Bugün" UTC günü değil, OKUL SAAT DİLİMİNDEKİ gündür (KK-205 / DK-03).
  // Tarayıcı yerelini varsayılan almak, 03:00'te açılan bir gün sınırında
  // sabah raporunu bir önceki güne yazardı.
  useEffect(() => {
    if (dokunuldu || !okul.data) return;
    setTarih(yerelGun(new Date(), okul.data.saat_dilimi));
  }, [okul.data, dokunuldu]);

  const rapor = useZRaporu(tarih, seciliNoktaId);

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Z raporu"
        aciklama="Gün sonu özeti: her nokta için ayrı, okul için konsolide. Konsolide satır, nokta kırılımının toplamıdır."
        eylem={
          <DisaAktar
            rapor="z"
            dosyaKoku={`z-raporu-${tarih}`}
            sorgu={{ tarih, nokta_id: seciliNoktaId ?? undefined }}
            devreDisi={!tarih}
          />
        }
      />

      <FiltreSeridi>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="z-tarih"
            className="text-sm font-semibold text-murekkep-2"
          >
            Tarih
          </label>
          <input
            id="z-tarih"
            type="date"
            value={tarih}
            onChange={(e) => {
              setDokunuldu(true);
              setTarih(e.target.value);
            }}
            className="min-h-hedef rounded-lg border border-cetvel bg-yuzey px-3 font-govde text-md text-murekkep"
          />
          {okul.data && (
            <p className="m-0 text-xs text-murekkep-3">
              Gün sınırı okul saat diliminde:{" "}
              <span className="font-mono">{okul.data.saat_dilimi}</span>
            </p>
          )}
        </div>
        {seciliNoktaId && (
          <Rozet tur="simit">nokta süzgeci sunucuda uygulanıyor</Rozet>
        )}
      </FiltreSeridi>

      <SorguDurumu
        yukleniyor={rapor.isLoading}
        hata={rapor.error}
        bos={!rapor.isLoading && (rapor.data?.noktalar.length ?? 0) === 0}
        bosBaslik="Bu gün için nokta yok"
        bosAciklama="Seçili günde aktif satış noktası bulunamadı."
        iskeletSatir={6}
        yenile={() => void rapor.refetch()}
      >
        {rapor.data && (
          <section className="flex flex-col gap-3">
            <header>
              <h2 className="m-0 text-xl">
                {rapor.data.okul_ad} · {gunGoster(rapor.data.tarih)}
              </h2>
              <p className="m-0 text-sm text-murekkep-3">
                Saat dilimi{" "}
                <span className="font-mono">{rapor.data.saat_dilimi}</span> ·
                ciroya yalnızca tamamlanmış satışlar girer; mutabakat bekleyen
                satışlar ayrı sayılır.
              </p>
            </header>

            {rapor.data.konsolide.mutabakat_bekleyen > 0 && (
              <Uyari tur="uyari" baslik="Kapanmamış iş var">
                Bu günde{" "}
                <span className="rakam font-mono">
                  {rapor.data.konsolide.mutabakat_bekleyen}
                </span>{" "}
                satış mutabakat bekliyor ve ciroya DAHİL DEĞİLDİR. Mutabakat
                kuyruğunu kapatmadan gün kapanmış sayılmaz.
              </Uyari>
            )}

            <Tablo>
              <TabloBaslik>
                <th scope="col">Nokta</th>
                <th scope="col">Ciro</th>
                <th scope="col">Kart</th>
                <th scope="col">Nakit</th>
                <th scope="col">İşlem</th>
                <th scope="col">İptal</th>
                <th scope="col">İade</th>
                <th scope="col">Nakit yükleme</th>
                <th scope="col">Vardiya farkı</th>
                <th scope="col">Offline satış</th>
                <th scope="col">Mutabakat</th>
              </TabloBaslik>
              <TabloGovde>
                {rapor.data.noktalar.map((n) => (
                  <NoktaSatiri key={n.nokta_id} satir={n} />
                ))}
                {/* Konsolide: kırılımın TOPLAMI (AK-13). */}
                <tr className="bg-kagit-2 font-semibold">
                  <th scope="row" className="px-3 py-2 text-left">
                    {rapor.data.okul_ad} (konsolide)
                  </th>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.ciro_toplam}
                      yonsuz
                      boy="lg"
                      etiket="konsolide ciro"
                    />
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.ciro_kart}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.ciro_nakit}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="rakam text-right font-mono">
                    {rapor.data.konsolide.islem_adedi}
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.iptal_toplam}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.iade_toplam}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.nakit_yukleme}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="text-right">
                    <VardiyaFarki deger={rapor.data.konsolide.vardiya_farki} />
                  </td>
                  <td className="rakam text-right font-mono">
                    {rapor.data.konsolide.offline_satis_adedi}
                  </td>
                  <td className="rakam text-right font-mono">
                    {rapor.data.konsolide.mutabakat_bekleyen}
                  </td>
                </tr>
              </TabloGovde>
            </Tablo>
          </section>
        )}
      </SorguDurumu>
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function NoktaSatiri({ satir }: { satir: ZRaporuNokta }) {
  return (
    <tr>
      <th scope="row" className="px-3 py-2 text-left font-normal">
        {satir.nokta_ad}
      </th>
      <td className="text-right">
        <Tutar
          deger={satir.ciro_toplam}
          yonsuz
          boy="md"
          etiket={`${satir.nokta_ad} cirosu`}
        />
      </td>
      <td className="text-right">
        <Tutar deger={satir.ciro_kart} yonsuz boy="sm" />
      </td>
      <td className="text-right">
        <Tutar deger={satir.ciro_nakit} yonsuz boy="sm" />
      </td>
      <td className="rakam text-right font-mono">{satir.islem_adedi}</td>
      <td className="text-right">
        <Tutar deger={satir.iptal_toplam} yonsuz boy="sm" />
      </td>
      <td className="text-right">
        <Tutar deger={satir.iade_toplam} yonsuz boy="sm" />
      </td>
      <td className="text-right">
        <Tutar deger={satir.nakit_yukleme} yonsuz boy="sm" />
      </td>
      <td className="text-right">
        <VardiyaFarki deger={satir.vardiya_farki} />
      </td>
      <td className="rakam text-right font-mono">
        {satir.offline_satis_adedi}
      </td>
      <td className="rakam text-right font-mono">{satir.mutabakat_bekleyen}</td>
    </tr>
  );
}

/**
 * KK-311: fark NOKTAYA yazılır. null "fark yok" DEĞİL, "vardiya henüz
 * kapanmadı" demektir; ikisi aynı hücrede aynı görünemez.
 */
function VardiyaFarki({ deger }: { deger: string | null }) {
  if (deger === null) {
    return <span className="text-xs text-murekkep-3">vardiya kapanmadı</span>;
  }
  // Fark defter kuralına TÂBİDİR: eksi fark kasadan para eksildi demektir.
  return <Tutar deger={deger} isaretiGoster boy="sm" etiket="vardiya farkı" />;
}
